import { t } from "@lingui/core/macro";
import React from "react";
import { createRoot } from "react-dom/client";
import "./index.scss";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { AuthProvider } from "react-oidc-context";
import { HelmetProvider } from "@dr.pogodin/react-helmet";
import { createBrowserRouter, Outlet, RouterProvider } from "react-router";
import { Index as PatientContainer } from ".";
import { Navigate } from "react-router";
import { Schedule } from "./routes/schedule/schedule";
import { Login } from "./routes/login";
import RequireAuth from "./components/requireAuth";
import Chat from "@/routes/chat/chat";
import { Profile } from "@/routes/profile/profile";
import { Review } from "./routes/profile/review";
import { VideoMeeting } from "@/routes/VideoMeeting/VideoMeeting";
import { ENVIRONMENT, userManager } from "./utils/envUtils";
import { Measurements } from "./routes/measurements/measurements";
import { finishActivityOccurrence } from "./api/activities";
import * as Sentry from "@sentry/react";
import { PatientNotes } from "@/routes/profile/patientNotes";
import { Guide } from "@/routes/profile/guide";
import { I18nApp } from "./I18nApp";
import { About } from "./routes/profile/about";
import { Trans } from "@lingui/react/macro";
import { isClientSideError } from "./utils/errorUtils";

Sentry.init({
  debug: import.meta.env.VITE_SENTRY,
  enabled: import.meta.env.VITE_SENTRY || import.meta.env.PROD,
  dsn: "https://40d0b2f282da3faed3f0bbd0d3061bd9@o4511035982479360.ingest.de.sentry.io/4511150458994768",
  environment: ENVIRONMENT,
  // Sentry's equivalent of the v10 defaults: v11 collects more by default, and
  // we have no need for more. User data is only what Sentry.setUser is given,
  // not, for example, the IP address. Opt in per key when a need arises.
  dataCollection: {
    userInfo: false,
    cookies: false,
    httpHeaders: {
      request: { deny: ["forwarded", "-ip", "remote-", "via", "-user"] },
      response: { deny: ["forwarded", "-ip", "remote-", "via", "-user"] },
    },
    httpBodies: [],
    urlQueryParams: { deny: ["forwarded", "-ip", "remote-", "via", "-user"] },
    genAI: { inputs: false, outputs: false },
    databaseQueryData: false,
    graphQL: { document: false, variables: false },
  },
  integrations: [Sentry.replayIntegration()],
  // Session Replay
  replaysSessionSampleRate: 0, // This sets the sample rate at 0%. You may want to change it to 100% while in development and then sample at a lower rate in production.
  replaysOnErrorSampleRate: 1.0, // If you're not already sampling the entire session, change the sample rate to 100% when sampling sessions where errors occur.
});

if (import.meta.env.DEV && import.meta.env.VITE_MSW) {
  const { worker, HttpNetworkFrame } = await import("./mocks/browser");
  await worker.start({
    onUnhandledFrame({ frame }) {
      if (
        frame instanceof HttpNetworkFrame &&
        frame.data.request.url.includes("medoma")
      ) {
        console.error(
          "Found an unhandled %s request to %s",
          frame.data.request.method,
          frame.data.request.url,
        );
      }
    },
  });
  worker.listHandlers();
}

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      retry: (count, error) => {
        return count < 3 && !isClientSideError(error);
      },
    },
  },
});

// Force stop background token refresh when Care is backgrounded
window.addEventListener("visibilitychange", () => {
  if (document.visibilityState === "hidden") {
    userManager.stopSilentRenew();
  } else {
    // Restart background token refresh when Care is foregrounded
    userManager.startSilentRenew();
    // Check for expired or missing token
    userManager
      .getUser()
      .then((user) => {
        if (!user || user.expired) {
          // Pre-emptively trigger an MFA check rather than waiting for a failed request
          void userManager.signinRedirect({
            state: window.location.pathname,
            acr_values: "mfa",
          });
        }
      })
      .catch(() => {
        void userManager.signinRedirect({
          state: window.location.pathname,
          acr_values: "mfa",
        });
      });
  }
});

// Navigation helper to allow the wrapper app to navigate to a specific route without reloading the whole app
window.onmessage = (event) => {
  if (event.origin !== window.origin) {
    return;
  }
  if (event.data.source !== "wrapper-app") {
    return;
  }

  console.log("Received message from wrapper app", event.data);

  // Old
  if (event.data.action === "navigate:video") {
    console.log("Navigating to video because of wrapper app command");
    void router.navigate("/video");
  }

  // Jitsi
  if (event.data.action === "navigate:video:jitsi") {
    console.log("Navigating to jitsi video because of wrapper app command");
    void router.navigate("/video?video-service=jitsi");
  }

  if (event.data.action === "navigate:schedule") {
    console.log("Navigating to schedule because of wrapper app command");
    void router.navigate("/schedule");
  }

  if (
    event.data.action === "finishActivityOccurrence" &&
    event.data.activityId &&
    event.data.occurrenceId &&
    typeof event.data.activityId === "string" &&
    typeof event.data.occurrenceId === "string"
  ) {
    console.log(
      `Finishing activity occurrence ${event.data.activityId} ${event.data.occurrenceId} because of wrapper app command`,
    );
    finishActivityOccurrence(event.data.activityId, event.data.occurrenceId)
      .then(() => queryClient.invalidateQueries({ queryKey: ["schedule"] }))
      .catch((_error: unknown) => {
        const errorMessage = t`Dina mätvärden har rapporterats in, men ett tekniskt fel gjorde att det inte gick att markera aktiviteten som klar. Skriv till oss i chatten så hjälper vi dig.`;
        Sentry.captureException(
          new Error(`An error alert was shown: ${errorMessage}`),
        );

        alert(errorMessage);
      });
  }
};

const router = createBrowserRouter([
  {
    path: "/",
    element: (
      <RequireAuth>
        <PatientContainer />
      </RequireAuth>
    ),
    errorElement: (
      <div>
        <Trans>
          <h1>Något gick fel</h1>
          <p>Ladda om sidan</p>
        </Trans>
      </div>
    ),
    children: [
      {
        index: true,
        element: <Navigate to="schedule" replace={true} />,
      },
      { path: "schedule", element: <Schedule /> },
      { path: "measurements", element: <Measurements /> },
      {
        path: "chat",
        element: <Chat />,
      },
      {
        path: "profile",
        element: <Outlet />,
        children: [
          {
            index: true,
            element: <Profile />,
          },
          {
            path: "patientNotes",
            element: <PatientNotes />,
          },
          {
            path: "patientGuide",
            element: <Guide />,
          },
          {
            path: "about",
            element: <About />,
          },
          {
            path: "review",
            element: <Review />,
          },
        ],
      },
      { path: "video", element: <VideoMeeting /> },
    ],
  },
  {
    path: "login",
    element: <Login />,
  },
  {
    path: "status",
    element: <p>OK</p>,
  },
]);

// https://github.com/authts/react-oidc-context/blob/f175dcba6ab09871b027d6a2f2224a17712b67c5/src/AuthProvider.tsx#L20-L30
const removeCodeAndStateParametersFromURL = () =>
  window.history.replaceState({}, document.title, window.location.pathname);

const root = createRoot(document.getElementById("root")!);
root.render(
  <React.StrictMode>
    <AuthProvider
      userManager={userManager}
      onSigninCallback={removeCodeAndStateParametersFromURL}
    >
      <QueryClientProvider client={queryClient}>
        <HelmetProvider>
          <I18nApp>
            <RouterProvider router={router} />
          </I18nApp>
        </HelmetProvider>
      </QueryClientProvider>
    </AuthProvider>
  </React.StrictMode>,
);
